import {
  ChangeDetectorRef,
  Directive,
  Inject,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import { ControlValueAccessor, FormControl } from '@angular/forms';
import { takeUntil } from 'rxjs';

import { RxntFormGroupService } from './directives/rxnt-form-group';
import { RxntSubscribeControl } from './rxnt-subscribe-control';

@Directive()
export abstract class RxntFormControl extends RxntSubscribeControl implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() reserveSpaceForError: boolean | null = null;
  @Input() isRequired: boolean | null = null;
  @Input() isRequiredMessage: string | null = null;

  formControl: FormControl;
  onChanged: any = null;

  protected focused = false;
  typedWhileFocused = false;

  private _touched: boolean = false;
  private cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  get touched(): boolean {
    return this._touched;
  }

  set touched(value: boolean) {
    this._touched = value;
    this.cdRef.markForCheck();
  }

  constructor(@Optional() @Inject(RxntFormGroupService) protected formGroupService: RxntFormGroupService | null) {
    super();
    this.formControl = new FormControl();
  }

  ngOnInit(): void {
    this.formGroupService?.register(this);

    this.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.focused) {
        this.typedWhileFocused = true;
      }
    });
  }

  override ngOnDestroy(): void {
    this.formGroupService?.unregister(this);
    super.ngOnDestroy();
  }

  get resolvedReserveSpaceForError(): boolean {
    return this.resolveValue(this.reserveSpaceForError, s => s.reserveSpaceForError, false);
  }

  get resolvedIsRequired(): boolean {
    return this.resolveValue(this.isRequired, s => s.isRequired, false);
  }

  get resolvedIsRequiredMessage(): string {
    return this.resolveValue(this.isRequiredMessage, s => s.isRequiredMessage, 'Required.');
  }

  protected resolveValue<T>(componentValue: T | null, serviceGetter: (s: RxntFormGroupService) => T, fallback: T): T {
    if (componentValue !== null) return componentValue;
    if (this.formGroupService) return serviceGetter(this.formGroupService);
    return fallback;
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;

    this.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.onChanged(value);
    });
  }

  registerOnTouched(fn: any): void {
    // do nothing, we do not support this feature app-wide
  }

  writeValue(obj: any): void {
    this.formControl.setValue(obj);
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.formControl.disable();
    } else {
      this.formControl.enable();
    }
  }
}
